import {
  DEBUG_MODE,
  getBuildVersion,
  getUrlSurvey,
  getUrlSurveyById,
  getUrlSurveyByPinIdExternal,
  getUrlSurveyByToken,
  runLocalTest,
} from '../spdConfig';

import { Loader } from './loader';
import * as Configuration from './testConfiguration';
import { DbTexts, Text, TextSelector } from './textSelector';

export type TestSignature = {
  id: string;
  url: string;
  name: string;
  average_duration?: number | null;
};

export enum ModeSurvey {
  Examined = 'examined',
  PsychologistFast = 'psychologistFast',
  PsychologistFull = 'psychologistFull',
}

export type SurveyJSON = {
  db_texts: DbTexts;
  exam: string;
  pin?: string;
  exam_number?: string;
  camera_required?: boolean;
  static?: boolean;
  lange?: Configuration.Lange;
  gender?: Configuration.Gender;
  start_page: TextSurveyTemplate;
  end_page: TextSurveyTemplate;
  time_goto_start: number;
  tests: TestSignature[];
  start_test: string;
  mode: ModeSurvey;
  date_of_birth?: string;
};

export type Supervisor = {
  name: string;
  role: string;
  photo: string;
};

type TextSurveyTemplate = {
  text_description?: Text | null;
  text_button?: Text | null;
  text_announcements_prefix?: Text | null;
  text_announcements_postfix?: Text | null;
  info_for_examined?: string;
  help_exams?: string;
  specialised_consultations?: string;
  external?: boolean;
};

export type DataSurveyTemplate = {
  start_page: boolean;
  supervisors?: Supervisor[] | [];
  version: string;
} & TextSurveyTemplate;

export type InfoAfterExam = {
  infoForExamined: string[];
  helpExams: number[];
  specialisedConsultations: number[];
};

const generateRandomLetters = () => {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  let randomLetters = '';

  for (let i = 0; i < 3; i++) {
    const randomIndex = Math.floor(Math.random() * alphabet.length);
    randomLetters += alphabet[randomIndex];
  }

  return randomLetters;
};

export class Survey {
  protected textSelector: TextSelector;
  protected surveyJson: SurveyJSON | null = null;
  protected postfixForId: string = generateRandomLetters();

  constructor(protected mode: ModeSurvey = ModeSurvey.Examined) {
    this.textSelector = new TextSelector();
  }

  public getModeSurvey() {
    return this.mode;
  }

  public async authorizePublicUser(
    pin: string,
    externalId: string,
  ): Promise<boolean> {
    DEBUG_MODE &&
      console.log(
        'Autoryzacja publiczna',
        runLocalTest(),
        getUrlSurveyByPinIdExternal() + '?pin=' + pin + '&examId=' + externalId,
      );
    this.surveyJson = await Loader.getSyncJSONWithVersion(
      getUrlSurveyByPinIdExternal() + '?pin=' + pin + '&examId=' + externalId,
      null,
      !runLocalTest(),
    );
    DEBUG_MODE && console.log('Survey', this.surveyJson);
    this.surveyJson &&
      (await this.textSelector.setDbTexts(this.surveyJson.db_texts));
    return this.surveyJson !== null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }

  public async authorizeByToken(token: string): Promise<boolean> {
    DEBUG_MODE &&
      console.log(
        'Autoryzacja z tokenem',
        getUrlSurveyByToken() + '?token=' + token,
      );
    this.surveyJson = await Loader.getSyncJSONWithVersion(
      getUrlSurveyByToken() + '?token=' + token,
      null,
      !runLocalTest(),
    );
    DEBUG_MODE && console.log('Survey', this.surveyJson);
    this.surveyJson &&
      (await this.textSelector.setDbTexts(this.surveyJson.db_texts));
    return this.surveyJson !== null;
  }

  public async authorize(pin: string): Promise<boolean> {
    DEBUG_MODE && console.log('Autoryzacja', getUrlSurvey() + '?pin=' + pin);
    this.surveyJson = await Loader.getSyncJSONWithVersion(
      getUrlSurvey() + '?pin=' + pin,
      null,
      !runLocalTest(),
    );
    DEBUG_MODE && console.log('Survey', this.surveyJson);
    this.surveyJson &&
      (await this.textSelector.setDbTexts(this.surveyJson.db_texts));
    return this.surveyJson !== null;
  }

  public async authorizeById(id: string): Promise<boolean> {
    DEBUG_MODE &&
      console.log(
        'authorizeById (mode consultant)',
        getUrlSurvey() + '?id=' + id,
      );
    this.surveyJson = await Loader.getSyncJSONWithVersion(
      getUrlSurveyById() + '?id=' + id,
      null,
      !runLocalTest(),
    );
    DEBUG_MODE && console.log('Survey', this.surveyJson);
    this.surveyJson &&
      (await this.textSelector.setDbTexts(this.surveyJson.db_texts));
    return true;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }

  public getId() {
    if (this.surveyJson?.static) {
      return this.surveyJson?.exam + this.postfixForId; //w przypadku statycznego badania losuję numer do testów
    }
    return this.surveyJson?.exam;
  }

  public getPin() {
    return this.surveyJson?.pin || null;
  }

  public getNumber() {
    if (this.surveyJson?.static) {
      return this.surveyJson?.exam_number + this.postfixForId; //w przypadku statycznego badania losuję numer do testów
    }
    return this.surveyJson?.exam_number || '';
  }

  public getBirthday() {
    return this.surveyJson?.date_of_birth || null;
  }

  public getCameraRequired() {
    return this.surveyJson?.camera_required || false;
  }

  public isSetGender() {
    return this.surveyJson?.gender ? true : false;
  }

  public getTimeGoToStart() {
    return this.surveyJson?.time_goto_start;
  }

  public getTextsForStartPage(supervisors: Supervisor[]): DataSurveyTemplate {
    const supervisorsWithRole = supervisors.map(supervisor => {
      return {
        ...supervisor,
        role: this.textSelector.getText(supervisor.role).text_presentation,
      };
    });

    return {
      start_page: true,
      supervisors: supervisorsWithRole,
      text_description:
        this.surveyJson?.start_page?.text_description &&
        this.textSelector.getText(this.surveyJson.start_page.text_description),
      text_button:
        this.surveyJson?.start_page?.text_button &&
        this.textSelector.getText(this.surveyJson.start_page.text_button),
      text_announcements_prefix:
        this.textSelector.getText('supervision_prefix'),
      text_announcements_postfix: this.textSelector.getText(
        'supervision_postfix',
      ),
      version: getBuildVersion(),
    };
  }

  // eslint-disable-next-line complexity
  public getTextsForEndPage(
    psychologist: boolean,
    external: boolean,
    info: InfoAfterExam | null,
  ): DataSurveyTemplate {
    if (psychologist) {
      return this.getTextsForPsychologistEndPage();
    }

    let infoForExaminedText = '';
    let helpExamsText = '';
    let specialisedConsultationsText = '';
    let announcement = false;
    if (info) {
      if (info.infoForExamined.length) {
        const list = info.infoForExamined.map(
          id => this.textSelector.getText(`${id}`).text_presentation,
        );
        const textPrefix = this.textSelector.getText(
          'info_for_examined_prefix',
        ).text_presentation;
        const textPostfix = this.textSelector.getText(
          'info_for_examined_postfix',
        ).text_presentation;
        infoForExaminedText = `${textPrefix}${list.join(', ')}${textPostfix}`;
        announcement = true;
      }

      if (info.helpExams.length) {
        const list = info.helpExams.map(
          id => this.textSelector.getText(`help_exam_${id}`).text_presentation,
        );

        const textPrefix =
          this.textSelector.getText('help_exams_prefix').text_presentation;
        const textPostfix =
          this.textSelector.getText('help_exams_postfix').text_presentation;
        helpExamsText = `${textPrefix}${list.join(', ')}${textPostfix}`;
        announcement = true;
      }

      if (info.specialisedConsultations.length) {
        const list = info.specialisedConsultations.map(
          id =>
            this.textSelector.getText(`specialised_consultation_${id}`)
              .text_presentation,
        );
        const textPrefix = this.textSelector.getText(
          'specialised_consultations_prefix',
        ).text_presentation;
        const textPostfix = this.textSelector.getText(
          'specialised_consultations_postfix',
        ).text_presentation;
        specialisedConsultationsText = `${textPrefix}${list.join(
          ', ',
        )}${textPostfix}`;
        announcement = true;
      }
    }

    return {
      external: external,
      start_page: false,
      text_description:
        this.surveyJson?.end_page?.text_description &&
        this.textSelector.getText(this.surveyJson.end_page.text_description),
      text_button:
        this.surveyJson?.end_page?.text_button &&
        this.textSelector.getText(this.surveyJson.end_page.text_button),
      text_announcements_prefix: announcement
        ? this.textSelector.getText('announcements_prefix')
        : null,
      text_announcements_postfix: announcement
        ? this.textSelector.getText('announcements_postfix')
        : null,
      info_for_examined: infoForExaminedText,
      specialised_consultations: specialisedConsultationsText,
      help_exams: helpExamsText,
      version: getBuildVersion(),
    };
  }

  protected getTextsForPsychologistEndPage(): DataSurveyTemplate {
    return {
      start_page: false,
      text_description:
        this.surveyJson?.end_page?.text_description &&
        this.textSelector.getText(
          'psychologist_' + this.surveyJson.end_page.text_description,
        ),
      text_button:
        this.surveyJson?.end_page?.text_button &&
        this.textSelector.getText(
          'psychologist_' + this.surveyJson.end_page.text_button,
        ),
      version: getBuildVersion(),
    };
  }

  public getTestSignatureByIndex(iTest: number): TestSignature | null {
    if (this.surveyJson && iTest < this.surveyJson.tests.length && iTest >= 0) {
      return this.surveyJson.tests[iTest];
    } else {
      return null;
    }
  }

  public getIndexTestbyIdent(ident: string): number {
    if (this.surveyJson) {
      for (let idx = 0; idx < this.surveyJson.tests.length; idx++) {
        if (this.surveyJson.tests[idx].id === ident) {
          return idx;
        }
      }
    }
    return -1;
  }

  public getIndexStartTest(): number {
    if (this.surveyJson) {
      return this.getIndexTestbyIdent(this.surveyJson.start_test);
    }
    return -1; // jeśli test nie został podany lub go nie znaleziono, nie zostanie rozpoczęty żaden test
  }

  public getLange() {
    return this.surveyJson?.lange || Configuration.Lange.PL;
  }

  public getGender() {
    return this.surveyJson?.gender || Configuration.Gender.Male;
  }

  public getTests() {
    return this.surveyJson?.tests || [];
  }
}
